// card-shell.ts — shared shell helpers for the cache-rendered caster cards
// (render-creature-card.ts + render-player-card.ts). Extracted verbatim from
// render-creature-card.ts (2026-06-12, plans/pc-sheet-full-ts-migration-session.md §2b):
// tiny PHP twins, the pane-mount emitter, shared catalog UUIDs, and the page-scoped
// style blocks the PHP includes re-emit per render.

import { itemsCol, layerOf, scalarLayerSlot, sourceOf } from '../shared/array-types';
import type { CachedItemNode } from './find-cached-item';

function recordOf(value: unknown): CachedItemNode | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as CachedItemNode
        : null;
}

// ── shared catalog constants (stable UUIDs, same values the PHP catalogs resolve) ────────────────
export const AB_SPOT_HIDDEN = 'B2D3417B-2700-11CB-E17D-38DECAF66F1A';   // UniversalCreatureAbilities spot_hidden_creatures
export const AB_SEARCH_HIDDEN_INV = '5B518ED7-4CEF-72BE-F20B-912598267714'; // search_for_hidden_inventory
export const AB_RESOLVE = 'B2BC3219-C745-7050-51DD-892951F0026B';
export const AB_SNAP_OUTTA_IT = '28D6D12E-A33F-4D3F-A668-C0C9037F5BC1';
export const AB_STOP_BLEEDING = '904F852B-443B-24E4-7B71-F785BB66433B';
export const AB_CUSTOM_FULL = 'C79A906F-BA10-B705-1CB9-6A7C5DFAEBEF';
export const AB_CUSTOM = 'E350D4F5-B29D-17C0-4AB4-0CAA0398C74A';
export const AB_CUSTOM_QUICK = '41FF250E-4146-C8C2-1324-DD6911BDB372';
export const AB_CUSTOM_FREE = 'EEB78637-0F73-8369-D230-8C29512B759E';
export const AB_CUSTOM_REACTION = 'BF71B9FC-32EF-29EF-9560-7219EBE600CD';
export const ST_BLEEDING = '3BE713AB-37D7-630B-C1F8-9470784BA33E';
export const ST_MODERATE_BLEEDING = '22E33A8E-DF5D-2596-8E2D-0E8A8528E47C';
export const ST_SEVERE_BLEEDING = 'BAAA237B-1AA7-0409-5BC7-1A6720573069';

// ── tiny local twins ─────────────────────────────────────────────────────────────────────────────
export function esc(v: unknown): string {
    return String(v ?? '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

/** icon_ph() twin — inert placeholder ftMountIcons upgrades via iconMap. */
export function iconPh(key: unknown, w = 25, h = 25, title?: string): string {
    const k = typeof key === 'object' && key !== null ? String(scalarLayerSlot(key) ?? '') : String(key ?? '');
    if (!k) return '';
    const t = title ? ` data-title='${esc(title)}'` : '';
    return `<i class='ft-icon' data-icon='${esc(k)}' data-w='${w}' data-h='${h}'${t} style='display:inline-block;width:${w}px;height:${h}px' aria-hidden='true'></i>`;
}

/** ft_parse_text_mount() twin — hydrated by window.ftMountParsedText. */
export function ptMount(text: unknown): string {
    const s = text === null || text === undefined ? '' : String(text);
    if (s === '') return '';
    const json = JSON.stringify({ text: s }).replace(/<\//g, '<\\/');
    return `<span class="ft-pt-mount"><script type="application/json" class="ft-pt-blob">${json}</script></span>`;
}

export function cookieVal(name: string): string | null {
    const m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]!) : null;
}

export function hexOf(id: string): string {
    return String(id).replace(/[^a-fA-F0-9]/g, '');
}

export function prettyNum(n: number): string {
    return n.toLocaleString('en-US');
}

/** float2rat() lite — CRs between 0 and 1 are simple fractions (1/2, 1/4, 1/8 …). */
export function fracOf(n: number): string {
    for (let d = 2; d <= 32; d++) {
        const num = n * d;
        if (Math.abs(num - Math.round(num)) < 1e-6) return `${Math.round(num)}/${d}`;
    }
    return String(n);
}

/** Bare-or-[0]-wrapped scalar off a bucket (encounter_creatures / player_characters fields). */
export function bucketScalar(bucket: unknown, key: string): string {
    const v = recordOf(bucket)?.[key];
    const out = Array.isArray(v) ? v[0] : v;
    return out === null || out === undefined ? '' : String(out);
}

/** has_status_effect() twin against the statuses.target rows. */
export function hasStatus(blob: unknown, statusItemId: string): boolean {
    const rows = recordOf(recordOf(blob)?.statuses)?.target;
    const rowsRecord = recordOf(rows);
    const list: unknown[] = Array.isArray(rows) ? rows : (rowsRecord ? Object.values(rowsRecord) : []);
    const want = statusItemId.toUpperCase();
    for (const row of list) {
        if (row === null || typeof row !== 'object') continue;
        const id = String(itemsCol(row, 'item_id') ?? recordOf(row)?.item_id ?? '').toUpperCase();
        if (id === want) return true;
    }
    return false;
}

/** ability_trigger_is_active() twin — blob.triggers rows; absent row = active. */
export function triggerIsActive(blob: unknown, abilityId: string): boolean {
    const triggers = recordOf(blob)?.triggers;
    const rows: unknown[] = Array.isArray(triggers) ? triggers : [];
    for (const row of rows) {
        if (String(layerOf(row, 'trigger_ability_id') ?? '') === abilityId) {
            const inactive = layerOf(row, 'trigger_inactive');
            return inactive === null || inactive === undefined || inactive === '' || Number(inactive) === 0;
        }
    }
    return true;
}

export function uuidOk(id: unknown): boolean {
    return typeof id === 'string'
        && /^[0-9A-Fa-f]{8}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{4}-[0-9A-Fa-f]{12}$/.test(id);
}

// ── carousel (render_carousel() twin, functions.php L15728) ───────────────────────────────────────
// Promoted to includes/js-src/shared/build-carousel.ts (2026-06-13, Phase 0) so ONE renderer serves
// both the cache cards (here) and the global window.ftMountCarousels hydrator. Re-exported so the
// card renderers keep importing it from './card-shell' unchanged.
export { buildCarousel, type CarouselSetPic } from '../shared/build-carousel';

// ── source footer (render_source_link() twin, build_source_arr.php L217) ──────────────────────────
// items.source is elided on the blob for N/A sources (build_item_arr), so a null source means N/A —
// synthesize the sentinel exactly like PHP's naSourceArr so the footer never silently vanishes.
const NA_SOURCE_ID = 'D887B8A7-2CED-AB81-3E4E-DDDF4ACD91AE';
export function sourceFooterHtml(blob: unknown): string {
    const src = recordOf(sourceOf(blob))
        ?? { source_id: NA_SOURCE_ID, source_name: 'N/A', source_abbreviation: 'NA' };
    const label = String(src.source_name ?? '').trim() || String(src.source_abbreviation ?? '').trim();
    const sourceId = String(src.source_id ?? '');
    const hasLink = uuidOk(sourceId);
    let inner: string;
    if (hasLink) {
        const href = `${BASE_URL}source.php?source_id=${encodeURIComponent(sourceId)}`;
        inner = `<a class="link-primary" href="${esc(href)}">${esc(label || 'Source')}</a>`;
    } else {
        if (label === '') return '';
        inner = esc(label);
    }
    return `<div class="card-footer text-right"><small>${inner}</small></div>`;
}

// ── pane mount emitter (no inline bootstrap script — the card calls ftMountPcSheet itself) ───────
export function paneMount(pane: string, casterId: string, extraAttrs = ''): string {
    return `<div class="ft-pc-sheet-pane" data-ft-pc-pane="${esc(pane)}" data-ft-caster-id="${esc(casterId)}"${extraAttrs}></div>`;
}

// ── page-scoped style blocks, ported from the PHP includes (they re-emit per render). They now
//    deliberately DIVERGE from the PHP sheet in one way: the card's Core Abilities banner is a
//    single COMPACT row (title + view toggle + search), so the tall pt/pb-1rem banner override is
//    omitted here and the abilities search overlay is 3rem instead of 3.5rem. ──────────────────────
export const ABILITIES_TAB_STYLE = `<style>
.ft-abilities-search-anchor { width: 100%; }
.ft-abilities-search-anchor .ft-sheet-search-icon { pointer-events: none; color: #555c64; opacity: 0.7; }
@media (min-width: 1200px) {
\t.ft-abilities-header-wrap { position: relative; }
\t.ft-abilities-header-wrap .ft-core-abilities > h5 { padding-right: min(17rem, 45%) !important; }
\t.ft-abilities-search-row { position: absolute !important; top: 0.3rem; left: 0; right: 0; bottom: auto; height: 3rem; margin: 0 !important; padding: 0 0.5rem !important; display: flex !important; align-items: center !important; justify-content: flex-end !important; z-index: 2; pointer-events: none; }
\t.ft-abilities-search-anchor { position: static !important; top: auto !important; right: auto !important; transform: none !important; width: 16rem; max-width: 40%; margin: 0 !important; pointer-events: auto; }
\t.ft-abilities-search-anchor .form-control { background-color: #212529; color: #fff; border-color: rgba(255, 255, 255, 0.15); box-shadow: none; height: 2.25rem; line-height: 1.25; }
\t.ft-abilities-search-anchor .form-control::placeholder { color: rgba(255, 255, 255, 0.6); }
}
</style>`;

export const WEAPONS_TAB_STYLE = `<style>
.ft-attacks-search-anchor { width: 100%; }
.ft-attacks-search-anchor .ft-sheet-search-icon { pointer-events: none; color: #555c64; opacity: 0.7; }
.ft-attacks-banner-wrap .fate-tide-sheet-ability-section > .fate-tide-attack-abilities-header-banner { display: flex; flex-wrap: wrap; align-items: center; justify-content: center; column-gap: 0.5rem; row-gap: 0.375rem; }
.ft-attacks-banner-wrap .fate-tide-attack-abilities-header-banner__title { flex: 0 1 auto; min-width: 0; }
.ft-attacks-banner-wrap .fate-tide-attack-abilities-header-banner__suffix { flex: 0 0 auto; }
@media (min-width: 1200px) {
\t.ft-attacks-banner-wrap .fate-tide-sheet-ability-section > .fate-tide-attack-abilities-header-banner { display: grid !important; grid-template-columns: 1fr auto 1fr !important; align-items: center !important; column-gap: 0.5rem !important; row-gap: 0 !important; padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; padding-left: 0.75rem !important; padding-right: 0.5rem !important; margin-top: 0.25rem !important; min-height: 3.5rem; box-sizing: border-box; }
\t.ft-attacks-banner-wrap .fate-tide-attack-abilities-header-banner__suffix { grid-column: 1 / 2 !important; grid-row: 1 !important; justify-self: start !important; align-self: center !important; }
\t.ft-attacks-banner-wrap .fate-tide-attack-abilities-header-banner__title { grid-column: 2 / 3 !important; grid-row: 1 !important; justify-self: center !important; align-self: center !important; display: flex !important; align-items: center !important; justify-content: center !important; min-width: 0 !important; line-height: 1.25 !important; }
\t.ft-attacks-banner-wrap .fate-tide-attack-abilities-header-banner__suffix .ft-class-mechanic-attacks-strip { align-items: center !important; }
\t.ft-attacks-banner-wrap { position: relative; }
\t.ft-attacks-banner-wrap .ft-pc-weapon-abilities { padding-left: 0; padding-right: 0; }
\t.ft-attacks-banner-wrap .ft-pc-weapon-abilities .fate-tide-sheet-ability-section:first-child > h5 { padding-top: 1rem !important; padding-bottom: 1rem !important; padding-right: min(17rem, 45%) !important; }
\t.ft-attacks-search-row { position: absolute !important; top: 0.55rem; left: 0; right: 0; bottom: auto; height: 3.5rem; margin: 0 !important; padding: 0 1rem 0 13.5rem !important; display: flex !important; align-items: center !important; justify-content: flex-end !important; z-index: 2; pointer-events: none; }
\t.ft-attacks-search-anchor { position: static !important; width: 16rem; max-width: 60%; margin: 0 !important; pointer-events: auto; }
\t.ft-attacks-search-anchor .form-control { background-color: #212529; color: #fff; border-color: rgba(255, 255, 255, 0.15); box-shadow: none; height: 2.25rem; line-height: 1.25; }
\t.ft-attacks-search-anchor .form-control::placeholder { color: rgba(255, 255, 255, 0.6); }
}
.ft-weapons-equipped--sideinfo-host .ft-weapons-tab-h,
.ft-weapons-equipped--sideinfo-host .fate-tide-sheet-ability-section > h5,
.ft-weapons-equipped--sideinfo-host .fate-tide-sheet-ability-section > .fate-tide-attack-abilities-header-banner { margin-top: 0.25rem !important; padding-top: 0.5rem !important; padding-bottom: 0.5rem !important; font-size: 0.95rem; line-height: 1.2; }
.ft-weapons-equipped--sideinfo-host .ft-attacks-banner-wrap .fate-tide-sheet-ability-section > .fate-tide-attack-abilities-header-banner { display: flex !important; flex-wrap: wrap !important; align-items: center !important; justify-content: center !important; min-height: 0 !important; }
.ft-weapons-equipped--sideinfo-host .ft-attacks-banner-wrap .fate-tide-sheet-ability-section > .fate-tide-attack-abilities-header-banner__title,
.ft-weapons-equipped--sideinfo-host .ft-attacks-banner-wrap .fate-tide-sheet-ability-section > .fate-tide-attack-abilities-header-banner__suffix { grid-column: unset !important; grid-row: unset !important; justify-self: unset !important; }
</style>`;
